import React, { useState } from 'react';
import { ethers } from 'ethers';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import { formatNumber, shortenAddress, formatDate, formatUserId } from '../../utils/formatters';
import { isRootId } from '../../config/packages';
import { useNotification } from '../../context/NotificationContext';
import { Section, InfoRow, StatGrid, PackageSelect } from './AdminUI';
import { fromWei, toWei, bpsToPct } from './useAdmin';

const AdminUsers = ({ admin }) => {
  const { read, runTx, pending } = admin;
  const { error: showError } = useNotification();

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [user, setUser] = useState(null);
  const [investPkg, setInvestPkg] = useState('10');

  const loadUser = async (q = query) => {
    const input = String(q).trim();
    if (!input) return;

    setSearching(true);
    try {
      let uid;
      if (ethers.utils.isAddress(input)) {
        uid = (await read.storage.addressToId(input)).toNumber();
      } else if (/^\d+$/.test(input)) {
        uid = parseInt(input, 10);
      } else {
        showError('Enter a user ID or wallet address');
        setSearching(false);
        return;
      }

      // Contract root (1000) is never shown - by ID or by its wallet
      if (isRootId(uid)) {
        setUser({ isRoot: true });
        setSearching(false);
        return;
      }

      const u = uid ? await read.storage.getUser(uid) : null;
      if (!u || u.wallet === ethers.constants.AddressZero) {
        showError('User not found');
        setUser(null);
        setSearching(false);
        return;
      }

      const [isBlacklisted, invCount, available, rate, ownerAddr, adminAddr] = await Promise.all([
        read.storage.blacklisted(uid),
        read.storage.getInvestmentCount(uid),
        read.views.getAvailableBreakdown(uid),
        read.views.getROIRate(uid),
        read.storage.owner(),
        read.storage.admin()
      ]);

      const investments = await Promise.all(
        Array.from({ length: invCount.toNumber() }, (_, i) => read.views.getInvestmentDetails(uid, i))
      );

      const wallet = u.wallet.toLowerCase();
      setUser({
        id: uid,
        raw: u,
        isBlacklisted,
        isProtected: wallet === ownerAddr.toLowerCase() || wallet === adminAddr.toLowerCase(),
        available,
        rate: rate.toNumber(),
        investments
      });
    } catch (err) {
      console.error('Admin user lookup error:', err);
      showError('Could not load user');
    }
    setSearching(false);
  };

  const toggleBlacklist = async () => {
    const next = !user.isBlacklisted;
    const ok = await runTx('blacklist', next ? 'Blacklist' : 'Unblacklist', (w) => w.storage.setBlacklist(user.id, next));
    if (ok) loadUser(user.id);
  };

  const virtualInvest = async () => {
    if (!window.confirm(`Add a virtual ${investPkg} USDT investment to user #${user.id}?`)) return;
    const ok = await runTx('vinvest', 'Virtual invest', (w) => w.logic.virtualInvest(user.id, toWei(investPkg)));
    if (ok) loadUser(user.id);
  };

  const u = user?.raw;

  return (
    <>
      <Section title="Find User">
        <Input
          placeholder="User ID or wallet address"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadUser()}
        />
        <Button fullWidth onClick={() => loadUser()} loading={searching}>Search</Button>
      </Section>

      {user?.isRoot && (
        <Section title="Direct Contract">
          <div className="admin-badges">
            <span className="admin-badge info">Contract</span>
          </div>
          <p className="admin-section-subtitle">
            This is the direct contract account - the root of the tree created when the contract was deployed.
            Its details are not shown.
          </p>
        </Section>
      )}

      {user && !user.isRoot && (
        <>
          <Section title={`User #${user.id}`}>
            <div className="admin-badges">
              <span className={`admin-badge ${u.active ? 'ok' : 'off'}`}>{u.active ? 'Active' : 'Inactive'}</span>
              {user.isBlacklisted && <span className="admin-badge bad">Blacklisted</span>}
              {user.isProtected && <span className="admin-badge info">Owner / Admin</span>}
            </div>
            <InfoRow label="Wallet" value={shortenAddress(u.wallet, 6)} mono />
            <InfoRow label="Sponsor" value={formatUserId(u.sponsorId)} />
            <InfoRow label="Placement parent" value={formatUserId(u.referrerId)} />
            <InfoRow label="Side" value={u.position === 0 ? 'Left' : 'Right'} />
            <InfoRow label="Left / Right child" value={`${formatUserId(u.leftChild)} / ${formatUserId(u.rightChild)}`} />
            <InfoRow label="Current Package" value={`${formatNumber(fromWei(u.currentPackage), 0)} USDT`} />
            <InfoRow label="Total Investment" value={`${formatNumber(fromWei(u.totalInvestment))} USDT`} />
            <InfoRow label="ROI Rate" value={`${bpsToPct(user.rate)}%`} />
            <InfoRow label="Directs (L / R)" value={`${u.directReferrals.toString()} (${u.leftDirects.toString()} / ${u.rightDirects.toString()})`} />
            <InfoRow label="Team Count" value={u.teamCount.toString()} />
            <InfoRow label="Direct Business" value={`${formatNumber(fromWei(u.directBusiness))} USDT`} />
            <InfoRow label="Business L / R (carry)" value={`${formatNumber(fromWei(u.leftBusiness), 0)} / ${formatNumber(fromWei(u.rightBusiness), 0)}`} />
            <InfoRow label="Business L / R (total)" value={`${formatNumber(fromWei(u.leftBusinessTotal), 0)} / ${formatNumber(fromWei(u.rightBusinessTotal), 0)}`} />
          </Section>

          <StatGrid items={[
            { label: 'Available', value: formatNumber(fromWei(user.available.totalAvailable)) },
            { label: 'Withdrawn', value: formatNumber(fromWei(u.totalWithdrawn)) },
            { label: 'ROI Earned', value: formatNumber(fromWei(u.roiEarned)) },
            { label: 'Binary Earned', value: formatNumber(fromWei(u.binaryEarned)) },
            { label: 'Referral Earned', value: formatNumber(fromWei(u.referralEarned)) },
            { label: 'Investments', value: user.investments.length }
          ]} />

          {user.investments.length > 0 && (
            <Section title="Investments">
              {user.investments.map((inv, i) => (
                <div key={i} className="admin-inv">
                  <div className="admin-inv-head">
                    <span>#{i + 1} · {formatNumber(fromWei(inv.amount), 0)} USDT</span>
                    <span className={`admin-badge ${inv.active ? 'ok' : 'off'}`}>
                      {inv.active ? 'Active' : 'Capped'}
                    </span>
                  </div>
                  <div className="admin-inv-meta">
                    {inv.invType === 1 ? 'Virtual' : 'Real'} · {formatDate(inv.timestamp.toNumber())} ·
                    earned {formatNumber(fromWei(inv.totalEarned))} / {formatNumber(fromWei(inv.cap300))}
                  </div>
                </div>
              ))}
            </Section>
          )}

          <Section title="Actions">
            <PackageSelect label="Virtual investment" value={investPkg} onChange={setInvestPkg} />
            <Button fullWidth variant="secondary" onClick={virtualInvest} loading={pending === 'vinvest'}>
              Add Virtual Investment
            </Button>
            <Button
              fullWidth
              variant={user.isBlacklisted ? 'success' : 'danger'}
              onClick={toggleBlacklist}
              loading={pending === 'blacklist'}
              disabled={user.isProtected}
            >
              {user.isBlacklisted ? 'Remove from Blacklist' : 'Blacklist User'}
            </Button>
          </Section>
        </>
      )}
    </>
  );
};

export default AdminUsers;
